/**
 * Cart Items Table Component
 * Displays cart items grouped by category with editable fields
 */
import React, { useState } from 'react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Trash2, Percent, IndianRupee, Pencil, Plus, Minus } from 'lucide-react';
import { formatINR, isCascadingCategory, useAuth } from '../../lib/store';
import { roundINR } from '../../lib/pdfFormat';
import { CART_CATEGORY_ORDER, CART_CATEGORY_DISPLAY_NAMES } from './constants';
import { EditCartItemDialog } from './EditCartItemDialog';

/**
 * For per-sq-ft / per-running-feet items the customer-facing Net Rate
 * should read in the same unit as the base rate (e.g. ₹108/sq ft after
 * a 10% discount on ₹120/sq ft). For all other rate types the saved
 * `net_rate` (which is per-unit already, e.g. per box / per kg) is fine.
 *
 * Returns `{ netRate, sizeLabel }`.
 */
function computeDisplayValues(item) {
  const rateType = String(item.rate_type || '').toLowerCase();
  const isSqft = rateType === 'per sq ft';
  const isRft = rateType === 'per running feet';
  const baseRate = parseFloat(item.rate) || 0;
  const calculated = parseFloat(item.calculated_rate) || 0;
  const saved = parseFloat(item.net_rate) || 0;
  let netRate = saved;

  if ((isSqft || isRft) && baseRate > 0) {
    // Effective discount factor — mirror the cart's calc.
    let factor = 1;
    let rupeeSubtraction = 0;  // iter 100 — rupee-mode cascading
    if (item.cascading_discount && isCascadingCategory(item.main_category)) {
      const cascMode = (item.cascading_discount_type || 'percent').toLowerCase();
      String(item.cascading_discount).split(/[+\s]+/).forEach(tok => {
        const p = parseFloat(tok);
        if (isNaN(p) || p <= 0) return;
        if (cascMode === 'rupees') rupeeSubtraction += p;
        else factor *= 1 - p / 100;
      });
    } else if ((parseFloat(item.discount_value) || 0) > 0) {
      const dv = parseFloat(item.discount_value);
      if (item.discount_type === 'percent') factor = 1 - dv / 100;
      else if (calculated > 0) factor = (calculated - dv) / calculated;
    }
    netRate = Math.max(0, baseRate * factor - rupeeSubtraction);
  }

  return {
    netRate,
    // Adhesives store weight/volume in `thickness` (e.g., "90 GMS", "500 ML",
    // "60 KG") — but the legacy add-flow inherits `size` = "8x4" from the
    // product's available_sizes which is meaningless for a tube/bottle. Use
    // thickness as the customer-facing size for adhesives.
    sizeLabel: item.main_category === 'Adhesives' && item.thickness ? item.thickness : (item.size || '-'),
  };
}

export function CartItemsTable({ 
  items, 
  sections,
  itemSections,
  updateItem, 
  removeItem,
  handleAssignSection,
  isComparisonMode = false,
  onReplaceProduct
}) {
  // Group items by category
  const groupedItems = items.reduce((acc, item) => {
    const cat = item.main_category || 'Others';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(item);
    return acc;
  }, {});
  
  // Sort categories by predefined order
  const sortedCategories = Object.keys(groupedItems).sort((a, b) => {
    const idxA = CART_CATEGORY_ORDER.indexOf(a);
    const idxB = CART_CATEGORY_ORDER.indexOf(b);
    return (idxA === -1 ? 999 : idxA) - (idxB === -1 ? 999 : idxB);
  });

  const [editingItem, setEditingItem] = useState(null);
  const openEditDialog = (item) => setEditingItem(item);
  const closeEditDialog = (next) => {
    if (!next) setEditingItem(null);
  };

  return (
    <div className="bg-white border border-[#E2E6D9] rounded-lg overflow-x-auto">
      <table className="w-full text-sm min-w-[840px]" data-testid="cart-table">
        <thead>
          <tr className="bg-[#F2F4EF] border-b border-[#E2E6D9]">
            <th className="text-left py-2 px-2 sm:px-3 text-[10px] sm:text-xs uppercase tracking-wide text-[#5C6150] font-semibold">Description</th>
            <th className="text-left py-2 px-2 sm:px-3 text-[10px] sm:text-xs uppercase tracking-wide text-[#5C6150] font-semibold">Size</th>
            <th className="text-right py-2 px-2 sm:px-3 text-[10px] sm:text-xs uppercase tracking-wide text-[#5C6150] font-semibold">Rate</th>
            <th className="text-center py-2 px-2 sm:px-3 text-[10px] sm:text-xs uppercase tracking-wide text-[#5C6150] font-semibold">Qty</th>
            <th className="text-center py-2 px-2 sm:px-3 text-[10px] sm:text-xs uppercase tracking-wide text-[#5C6150] font-semibold">Unit</th>
            <th className="text-center py-2 px-2 sm:px-3 text-[10px] sm:text-xs uppercase tracking-wide text-[#5C6150] font-semibold">Disc</th>
            <th className="text-right py-2 px-2 sm:px-3 text-[10px] sm:text-xs uppercase tracking-wide text-[#5C6150] font-semibold">Net Rate</th>
            <th className="text-right py-2 px-2 sm:px-3 text-[10px] sm:text-xs uppercase tracking-wide text-white bg-[#B93838] font-semibold" title="Per-line Special Discount — admin/manager editable">Spl Disc</th>
            <th className="text-right py-2 px-2 sm:px-3 text-[10px] sm:text-xs uppercase tracking-wide text-[#5C6150] font-semibold">Amount</th>
            {sections.length > 0 && (
              <th className="text-center py-2 px-1 text-[10px] sm:text-xs uppercase tracking-wide text-[#5C6150] font-semibold w-20">Room</th>
            )}
            <th className="w-8 sm:w-10"></th>
          </tr>
        </thead>
        <tbody>
          {sortedCategories.flatMap(category => [
            // Category subheading row
            <tr key={`cat-${category}`} className="bg-[#DAA520]/25 border-b border-[#E2E6D9]">
              <td colSpan={sections.length > 0 ? 11 : 10} className="py-2 px-3">
                <span className="text-xs font-bold uppercase tracking-wider text-[#1A1C16]">
                  {CART_CATEGORY_DISPLAY_NAMES[category] || category}
                </span>
              </td>
            </tr>,
            // Items in this category
            ...groupedItems[category].map((item) => (
              <CartItemRow 
                key={item.cart_id}
                item={item}
                sections={sections}
                itemSections={itemSections}
                updateItem={updateItem}
                removeItem={removeItem}
                handleAssignSection={handleAssignSection}
                isComparisonMode={isComparisonMode}
                onReplaceProduct={onReplaceProduct}
                onEdit={openEditDialog}
              />
            ))
          ])}
        </tbody>
      </table>
      <EditCartItemDialog
        open={!!editingItem}
        onOpenChange={closeEditDialog}
        item={editingItem}
        updateItem={updateItem}
      />
    </div>
  );
}

function CartItemRow({ item, sections, itemSections, updateItem, removeItem, handleAssignSection, isComparisonMode, onReplaceProduct, onEdit }) {
  // Role gate for per-line Special Discount. Admin/manager
  // can type; staff sees the value but the input is disabled.
  const { user } = useAuth();
  const canEditSpl = ['admin', 'manager'].includes(user?.role);
  // Local draft for the quantity input so the user can transiently clear it
  // (backspace the existing "1" to type "5"). The committed `item.quantity`
  // only changes when the draft parses to a valid number; on blur an empty
  // draft snaps back to the last good value.
  const allowDecimal = ['per kg', 'per running feet', 'per ltr', 'per gms', 'per ml'].includes(item.rate_type);
  const [qtyDraft, setQtyDraft] = useState(String(item.quantity ?? (allowDecimal ? '0.01' : '1')));
  // Re-sync draft when the canonical quantity changes from outside (e.g. a
  // discount toggle that rounds the qty, or loading a saved quotation).
  React.useEffect(() => {
    setQtyDraft(String(item.quantity ?? (allowDecimal ? '0.01' : '1')));
  }, [item.quantity, allowDecimal]);

  const onQtyChange = (e) => {
    const raw = e.target.value;
    setQtyDraft(raw);
    // Empty / partial input → do NOT push to the cart yet; let the user
    // keep typing. Commit only when we have a parseable positive number.
    if (raw === '' || raw === '-' || raw === '.' || raw.endsWith('.')) return;
    if (allowDecimal) {
      const f = parseFloat(raw);
      if (!isNaN(f) && f >= 0.01) updateItem(item.cart_id, { quantity: f });
    } else {
      const n = parseInt(raw, 10);
      if (!isNaN(n) && n >= 1) updateItem(item.cart_id, { quantity: n });
    }
  };
  const onQtyBlur = () => {
    // If the user left the field blank/invalid, fall back to the canonical
    // value from the cart line.
    if (qtyDraft === '' || qtyDraft === '-' || qtyDraft === '.') {
      setQtyDraft(String(item.quantity ?? (allowDecimal ? '0.01' : '1')));
      return;
    }
    if (allowDecimal) {
      const f = parseFloat(qtyDraft);
      if (isNaN(f) || f < 0.01) {
        setQtyDraft(String(item.quantity ?? '0.01'));
      } else {
        setQtyDraft(String(f));
      }
    } else {
      const n = parseInt(qtyDraft, 10);
      if (isNaN(n) || n < 1) {
        setQtyDraft(String(item.quantity ?? '1'));
      } else {
        setQtyDraft(String(n));
      }
    }
  };

  // ── Inline +/− steppers for fast qty nudging at the counter ──
  // Step is 0.5 for kg/feet/litre items and 1 for everything else.
  // The minimum floor matches onQtyBlur (0.01 / 1).
  const stepBy = (delta) => {
    const current = parseFloat(qtyDraft);
    const base = isNaN(current) ? (item.quantity ?? (allowDecimal ? 0.01 : 1)) : current;
    const step = allowDecimal ? 0.5 : 1;
    const floor = allowDecimal ? 0.01 : 1;
    let next = base + delta * step;
    if (next < floor) next = floor;
    // Round to 2 dp for decimals to avoid 0.30000000004 artifacts.
    if (allowDecimal) next = Math.round(next * 100) / 100;
    setQtyDraft(String(next));
    updateItem(item.cart_id, { quantity: next });
  };

  const isLaminateOrVeneer = ['Laminates', 'Veneers', 'Highlighters'].includes(item.main_category);
  const isCascadingCat = isCascadingCategory(item.main_category);
  const { netRate: displayNetRate, sizeLabel } = computeDisplayValues(item);

  return (
    <tr className="border-b border-[#E2E6D9] hover:bg-[#F9FAF7]" data-testid={`cart-item-${item.cart_id}`}>
      <td className="py-2 px-3 align-top">
        <div className="flex items-start gap-2">
          <div className="flex-1 min-w-0 break-words">
            {isLaminateOrVeneer ? (
              <>
                {item.product_code ? (
                  <p className="font-semibold text-[#1A1C16] text-xs font-mono">{item.product_code}</p>
                ) : (
                  <p className="font-semibold text-[#1A1C16] text-xs">{item.species || item.description}</p>
                )}
                {item.species && item.product_code && (item.show_brand !== false) && (
                  <p className="text-[#5C6150] text-xs">{item.species}</p>
                )}
                {(item.show_brand !== false) && (item.brand || item.description) && (
                  <p className="text-[#8F9485] text-[10px] italic">({item.brand} {item.description})</p>
                )}
              </>
            ) : (
              <>
                {item.brand && (item.show_brand !== false) ? (
                  <>
                    <p className="font-semibold text-[#1A1C16] text-xs">{item.brand}</p>
                    <p className="text-[#5C6150] text-xs">{item.description}</p>
                  </>
                ) : (
                  // Brand hidden by Admin setting on this product, or no brand at all
                  <p className="font-semibold text-[#1A1C16] text-xs">{item.description || item.brand || '-'}</p>
                )}
                {item.thickness && <span className="text-[#8F9485] text-xs">{item.thickness}</span>}
                {/* Wooden Flooring: show the box ↔ sq-ft conversion so the
                    salesperson can sanity-check at-a-glance. PDF renders
                    the same line. */}
                {item.wooden_flooring && item.wooden_flooring.sqft_per_box > 0 && (
                  <p className="text-[10px] text-[#8F9485] mt-0.5" data-testid={`cart-item-flooring-${item.cart_id}`}>
                    1 box = {item.wooden_flooring.sqft_per_box} sq ft &nbsp;|&nbsp;{' '}
                    {item.wooden_flooring.qty_boxes} box{item.wooden_flooring.qty_boxes !== 1 ? 'es' : ''} = {item.wooden_flooring.qty_sqft} sq ft
                  </p>
                )}
              </>
            )}
          </div>
          {/* Replace button for comparison mode */}
          {isComparisonMode && onReplaceProduct && (
            <button
              onClick={() => onReplaceProduct(item.cart_id, item.main_category)}
              className="px-1.5 py-0.5 text-[10px] bg-[#DAA520]/20 text-[#8B6914] rounded border border-[#DAA520]/30 hover:bg-[#DAA520]/30 transition-colors flex-shrink-0"
              title="Replace with different brand"
              data-testid={`replace-btn-${item.cart_id}`}
            >
              Replace
            </button>
          )}
        </div>
      </td>
      <td className="py-2 px-3 font-mono-data text-xs">{sizeLabel}</td>
      <td className="py-2 px-3 text-right font-mono-data text-xs">
        {formatINR(item.rate)}
        {item.rate_type && <span className="text-[#8F9485] text-[10px]">/{(item.rate_type.replace('per ', '') === 'running feet') ? 'RFT' : item.rate_type.replace('per ', '')}</span>}
      </td>
      <td className="py-2 px-3 text-center">
        {/* Quantity input with inline ± steppers.
            • The input uses a local `qtyDraft` string so backspacing the
              existing value to type a new one works (the field doesn't
              snap back to 1 mid-keystroke).
            • The − and + buttons let the staff nudge qty by 1 (or 0.5 for
              kg/feet/litre) without retyping — handy on phones at the
              counter. Floor is 1 (or 0.01 for decimals). */}
        <div className="inline-flex items-center gap-1 mx-auto">
          <button
            type="button"
            onClick={() => stepBy(-1)}
            className="h-7 w-6 flex items-center justify-center rounded border border-[#E2E6D9] bg-[#F9FAF7] hover:bg-[#EEF1E7] text-[#3B4029] text-xs disabled:opacity-40 disabled:cursor-not-allowed"
            disabled={
              allowDecimal
                ? (parseFloat(qtyDraft || item.quantity) || 0) <= 0.01
                : (parseInt(qtyDraft || item.quantity, 10) || 0) <= 1
            }
            aria-label="Decrease quantity"
            data-testid={`cart-qty-dec-${item.cart_id}`}
          >
            <Minus className="w-3 h-3" />
          </button>
          <Input
            type="number"
            min={allowDecimal ? 0.01 : 1}
            step={allowDecimal ? 0.5 : 1}
            value={qtyDraft}
            onChange={onQtyChange}
            onBlur={onQtyBlur}
            className="w-14 h-7 text-center text-xs bg-[#F9FAF7] border-[#E2E6D9] px-1"
            data-testid={`cart-qty-${item.cart_id}`}
          />
          <button
            type="button"
            onClick={() => stepBy(1)}
            className="h-7 w-6 flex items-center justify-center rounded border border-[#E2E6D9] bg-[#F9FAF7] hover:bg-[#EEF1E7] text-[#3B4029] text-xs"
            aria-label="Increase quantity"
            data-testid={`cart-qty-inc-${item.cart_id}`}
          >
            <Plus className="w-3 h-3" />
          </button>
        </div>
      </td>
      <td className="py-2 px-3 text-center text-xs text-[#5C6150]">{item.unit === 'SHT' ? 'SHEET' : item.unit}</td>
      <td className="py-2 px-3">
        {isCascadingCat ? (
          <div className="flex items-center gap-1 justify-center">
            <Select
              value={(item.cascading_discount_type || 'percent')}
              onValueChange={(v) => updateItem(item.cart_id, {
                cascading_discount_type: v,
                discount_type: 'cascading',
              })}
            >
              <SelectTrigger
                className="w-11 h-7 text-xs border-[#E2E6D9] p-1"
                data-testid={`cart-cascading-mode-${item.cart_id}`}
                title="Toggle between % and ₹ cascading"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="percent">%</SelectItem>
                <SelectItem value="rupees">₹</SelectItem>
              </SelectContent>
            </Select>
            <Input
              type="text"
              value={item.cascading_discount || ''}
              onChange={(e) => {
                let value = e.target.value.replace(/\s+/g, '+');
                value = value.replace(/\++/g, '+');
                value = value.replace(/^\+/, '');
                updateItem(item.cart_id, { 
                  cascading_discount: value,
                  discount_type: 'cascading',
                  discount_value: 0
                });
              }}
              placeholder=""
              className="w-20 h-7 text-xs text-center bg-[#F9FAF7] border-[#E2E6D9] font-medium"
              data-testid={`cart-discount-${item.cart_id}`}
              title={
                (item.cascading_discount_type || 'percent') === 'rupees'
                  ? 'Cascading rupees: e.g. 25+10 → ₹25 + ₹10 off per unit'
                  : 'Cascading: type 10 3 3 for 10+3+3%'
              }
            />
          </div>
        ) : (
          <div className="flex items-center gap-1 justify-center">
            <Select
              value={item.discount_type}
              onValueChange={(v) => updateItem(item.cart_id, { discount_type: v })}
            >
              <SelectTrigger className="w-12 h-7 text-xs border-[#E2E6D9] p-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="percent"><Percent className="w-3 h-3" /></SelectItem>
                <SelectItem value="amount"><IndianRupee className="w-3 h-3" /></SelectItem>
              </SelectContent>
            </Select>
            <Input
              type="number"
              min="0"
              value={item.discount_value || ''}
              onChange={(e) => updateItem(item.cart_id, { discount_value: parseFloat(e.target.value) || 0 })}
              placeholder=""
              className="w-16 h-7 text-xs text-center bg-[#F9FAF7] border-[#E2E6D9]"
              data-testid={`cart-discount-${item.cart_id}`}
            />
          </div>
        )}
      </td>
      <td className="py-2 px-3 text-right font-mono-data text-xs font-semibold">{formatINR(displayNetRate)}</td>
      {/* iter-133 / iter-134: per-line Special Discount cell. Admin/
          manager can edit, staff sees the value but the input is
          disabled. Type toggle (%/₹) mirrors the Additional Discount
          control in CartTotals. Value is a positive number; the
          formula is applied at the ONE authoritative Amount-writer
          in lib/store.js::updateItem. */}
      <td className="py-2 px-1 text-right" data-testid={`cart-spl-disc-cell-${item.cart_id}`}>
        <div className="flex items-center gap-1 justify-end">
          <Select
            value={item.special_discount_type || 'amount'}
            onValueChange={(v) => updateItem(item.cart_id, { special_discount_type: v })}
            disabled={!canEditSpl}
          >
            <SelectTrigger className={`w-12 h-7 text-xs px-1 border-[#E2E6D9] ${canEditSpl ? 'bg-white' : 'bg-[#F2F4EF] cursor-not-allowed'}`} data-testid={`cart-spl-disc-type-${item.cart_id}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="percent">%</SelectItem>
              <SelectItem value="amount">₹</SelectItem>
            </SelectContent>
          </Select>
          <Input
            type="number"
            min="0"
            step="1"
            value={item.special_discount || ''}
            onChange={(e) => updateItem(item.cart_id, { special_discount: Math.max(0, parseFloat(e.target.value) || 0) })}
            disabled={!canEditSpl}
            placeholder="—"
            className={`w-16 h-7 text-xs text-right font-mono-data ${(Number(item.special_discount) || 0) > 0 ? 'text-[#B93838] font-semibold border-[#B93838]/40' : 'text-[#8F9485]'} ${canEditSpl ? 'bg-white' : 'bg-[#F2F4EF] cursor-not-allowed'}`}
            data-testid={`cart-spl-disc-${item.cart_id}`}
            title={canEditSpl ? 'Special Discount applied to this line only' : 'Only admin/manager can edit'}
          />
        </div>
      </td>
      <td className="py-2 px-3 text-right font-mono-data text-xs font-bold text-[#556B2F]">{formatINR(roundINR(item.amount))}</td>
      {sections.length > 0 && (
        <td className="py-2 px-1 text-center">
          <Select 
            value={itemSections[item.cart_id] || item.section || 'none'} 
            onValueChange={(v) => handleAssignSection(item.cart_id, v === 'none' ? '' : v)}
          >
            <SelectTrigger className="h-6 w-16 text-[10px] border-[#E2E6D9] p-1">
              <SelectValue placeholder="-" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">-</SelectItem>
              {sections.map(s => (
                <SelectItem key={s} value={s} className="text-xs">{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </td>
      )}
      <td className="py-2 px-1">
        <div className="flex items-center gap-0.5 justify-end">
          {/* Edit pencil — ONLY for quick-add (custom) items, not for
              catalog products. Catalog items have a master record so we
              shouldn't let users mutate description/code/brand from the
              cart; they'd silently diverge from the DB. */}
          {onEdit && item.is_quick_add && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0 text-[#5C6150] hover:bg-[#F2F4EF]"
              onClick={() => onEdit(item)}
              data-testid={`cart-edit-${item.cart_id}`}
              title="Edit this custom item"
            >
              <Pencil className="w-3 h-3" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0 text-[#B93838] hover:bg-red-50"
            onClick={() => removeItem(item.cart_id)}
            data-testid={`cart-remove-${item.cart_id}`}
          >
            <Trash2 className="w-3 h-3" />
          </Button>
        </div>
      </td>
    </tr>
  );
}

export default CartItemsTable;
